import { getAutomaticDiscountsByAccount } from '@/services/discounts/discountServices';
import { Discount, DiscountType, AmountUnitType, MultiParticipantRule, EarlyRegistrationRule } from '@/Model/discounts/types';
import APP_UTILITIES from '@/utilities/commonFunctions';
import { AxiosResponse } from 'axios';
import store from '@/store';
import { getModule, Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';

/**
 * Vuex module for managing discounts.
 * @module discountStore
 */
@Module({
  namespaced: true,
  name: 'discountStore',
  store,
  dynamic: true,
})
class DiscountsModule extends VuexModule {
  discountList: Discount[] = [];
  isDiscountRequestInProgress: boolean = false;

  /**
   * Gets the list of discounts.
   * @returns {Discount[]} The list of discounts.
   */
  get discountListData(): Discount[] {
    return this.discountList;
  }

  /**
   * Checks if a discount request is in progress.
   * @returns {boolean} True if a discount request is in progress, false otherwise.
   */
  get discountRequestInProgress(): boolean {
    return this.isDiscountRequestInProgress;
  }

  /**
   * Gets the data for a specific type of discount.
   * @param {DiscountType} discountType - The type of discount to find.
   * @returns {Discount | null} The discount data, or null if it doesn't exist.
   */
  get getDiscountData() {
    return (discountType: DiscountType): Discount | null => {
      const discount = this.discountList.find(
        (discount) => discount.discountTypeId === discountType && discount.isActive
      );
      return discount || null;
    };
  }

  /**
   * Gets the data for the early registration discount.
   * @returns {Discount | null} The early registration discount data, or null if it doesn't exist.
   */
  get earlyDiscountData(): Discount | null {
    return this.getDiscountData(DiscountType.EARLY_REGISTRATION);
  }

  /**
   * Gets the data for the multi-participant discount.
   * @returns {Discount | null} The multi-participant discount data, or null if it doesn't exist.
   */
  get multiParticipantDiscountData(): Discount | null {
    return this.getDiscountData(DiscountType.MULTI_PARTICIPANT);
  }

  /**
   * Checks if a multi-participant discount exists.
   * @returns {boolean} True if a multi-participant discount exists, false otherwise.
   */
  get doesMultiParticipantDiscountExist(): boolean {
    return this.getDiscountData(DiscountType.MULTI_PARTICIPANT) !== null;
  }

  /**
   * Gets the discount description text.
   * @returns {string} The discount description text.
   */
  get discountDescriptionText(): string {
    let discountStrDescription = '';

    if (this.multiParticipantDiscountData) {
      this.multiParticipantDiscountData.rules.forEach(
        (rule: MultiParticipantRule | EarlyRegistrationRule, index: number) => {
          if (index > 0) {
            discountStrDescription += ', ';
          }

          discountStrDescription += APP_UTILITIES.addSymbol(
            rule.amount,
            this.multiParticipantDiscountData !== null &&
              this.multiParticipantDiscountData.amountUnitType === AmountUnitType.FIXED_AMOUNT
          );
          discountStrDescription += ` off when you register ${(rule as MultiParticipantRule).participants} participants`;
        }
      );
    }
    return discountStrDescription;
  }

  /**
   * Sets the list of discounts.
   * @param {Discount[]} discounts - The list of discounts.
   */
  @Mutation
  setDiscountList(discounts: Discount[]): void {
    this.discountList = discounts;
  }

  /**
   * Sets the state of the discount request in progress.
   * @param {boolean} isInProgress - The state of the discount request in progress.
   */
  @Mutation
  setDiscountRequestInProgress(isInProgress: boolean): void {
    this.isDiscountRequestInProgress = isInProgress;
  }

  /**
   * Fetches discount information for a given account.
   * @param {number} accountId - The ID of the account to fetch discounts for.
   * @returns {Promise<void>} A promise that resolves when the discount information is fetched.
   */
  @Action
  async getDiscountInfo(accountId: number): Promise<void> {
    try {
      this.context.commit('setDiscountRequestInProgress', true);
      const response: AxiosResponse<Discount[]> = await getAutomaticDiscountsByAccount(accountId);
      this.context.commit('setDiscountList', response.data);
    }
    catch (error) {
      console.error('Error getting the discounts data', error);
    }
    finally {
      this.context.commit('setDiscountRequestInProgress', false);
    }
  }
}

export default getModule(DiscountsModule);
